import { useState, useEffect, useRef, useLayoutEffect, useMemo, Fragment } from "react";
import { createPortal } from "react-dom";
import { Paper, Tooltip } from "@mui/material";
import {
  Spinner, Modal, ModalHeader, ModalBody, ModalFooter, Button,
} from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  deleteInvoice,
  updateInvoiceStatus,
  addInvoiceComment,
  approveInvoice,
  declineInvoice,
  markInvoicePaid,
  setInvoiceEnteredAcct,
  bulkMarkInvoicesPaid,
  bulkApproveInvoices,
  getInvoiceSignedUrls,
  INVOICE_STATUSES,
  statusStyle,
  formatCurrency,
  formatInvoiceNo,
} from "./Api";
import { InvoiceFormModal } from "./InvoiceFormModal";
import { readInvoiceDraft, discardInvoiceDraft } from "./invoiceDraft";
import { InvoiceDetailDrawer } from "./InvoiceDetailDrawer";
import { InvoiceForwardModal } from "./InvoiceForwardModal";
import { PayrollCalendarModal } from "./PayrollCalendarModal";
import { Previewer } from "../../../../components/DocumentPreviewer/Previewer";
import { getFileExtension, imageTypes, pdfTypes, officeTypes, videoTypes }
  from "../../../../components/DocumentPreviewer/utils";

const STATUS_VALUES = INVOICE_STATUSES.map((s) => s.value);

const getFileTypeFromKey = (keyOrName) => {
  const ext = getFileExtension(keyOrName).toLowerCase();
  if (pdfTypes.includes(ext)) return "pdf";
  if (imageTypes.includes(ext)) return "image";
  if (videoTypes.includes(ext)) return "video";
  if (officeTypes.includes(ext)) return "office";
  return "unknown";
};

// Submitted On is the real moment of submission — shown in the viewer's local time.
const fmtSubmitted = (inv) => {
  const d = inv?.createdAt || inv?.invoiceDate;
  return d ? moment(d).format("MMM D, YYYY") : "—";
};

const payable = (inv) => (inv?.total != null ? inv.total : inv?.amount);

// Derived overdue: an unpaid (not declined) invoice whose payment date has arrived.
const isOverdue = (inv) => {
  if (!inv?.dueDate || inv.status === "Paid" || inv.status === "Declined") return false;
  return moment.utc(inv.dueDate).startOf("day").valueOf() <= moment().startOf("day").valueOf();
};

const OverdueChip = () => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5",
    borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
  }}>
    <FeatherIcon icon="alert-triangle" size={10} /> Overdue
  </span>
);

const SyncedScroll = ({ children, maxHeight }) => {
  const contentRef = useRef(null);
  const [cursor, setCursor] = useState("grab");
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ active: false, startX: 0, scrollLeft: 0, moved: false });

  useEffect(() => {
    const onMove = (e) => {
      if (!drag.current.active) return;
      const el = contentRef.current;
      if (!el) return;
      const dist = e.pageX - drag.current.startX;
      if (Math.abs(dist) > 4) {
        drag.current.moved = true;
        el.scrollLeft = drag.current.scrollLeft - dist;
      }
    };
    const onUp = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      setCursor("grab");
      setDragging(false);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest("button, a, input, select, label, [role='button']")) return;
    const el = contentRef.current;
    if (!el) return;
    drag.current = { active: true, startX: e.pageX, scrollLeft: el.scrollLeft, moved: false };
    setCursor("grabbing");
    setDragging(true);
  };

  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  const showHandle = hovered && !dragging;

  return (
    <div
      style={{ position: "relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={contentRef}
        onMouseDown={onMouseDown}
        onClickCapture={onClickCapture}
        style={{
          overflowX: "auto",
          overflowY: maxHeight ? "auto" : "visible",
          maxHeight: maxHeight || "none",
          cursor,
          userSelect: "none",
        }}
      >
        {children}
      </div>

      {/* Floating drag handle — appears on hover, hides while dragging */}
      <div style={{
        position: "absolute",
        bottom: 14,
        left: "50%",
        transform: `translateX(-50%) translateY(${showHandle ? 0 : 6}px)`,
        opacity: showHandle ? 1 : 0,
        pointerEvents: "none",
        transition: "opacity 0.18s ease, transform 0.18s ease",
        zIndex: 20,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "rgba(15,23,42,0.78)",
        backdropFilter: "blur(6px)",
        color: "#fff",
        borderRadius: 20,
        padding: "5px 14px",
        fontSize: "0.68rem",
        fontWeight: 700,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 16px rgba(15,23,42,0.22)",
        letterSpacing: "0.2px",
      }}>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
          <path d="M1 5h12M1 5l3-3M1 5l3 3M13 5l-3-3M13 5l-3 3"
            stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Hold &amp; drag to scroll
      </div>
    </div>
  );
};

const StyledCheckbox = ({ checked, onChange, disabled, label }) => (
  <label style={{ display: "inline-flex", alignItems: "center", cursor: disabled ? "not-allowed" : "pointer", margin: 0 }} aria-label={label}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{ position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none" }}
      aria-label={label}
    />
    <span style={{
      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      border: checked ? "2px solid #4F46E5" : "2px solid #CBD5E1",
      background: checked ? "#4F46E5" : "#fff",
      transition: "all 0.12s ease",
      opacity: disabled ? 0.4 : 1,
      boxShadow: checked ? "0 0 0 3px rgba(79,70,229,0.15)" : "none",
    }}>
      {checked && (
        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4L3.5 6.5L9 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  </label>
);

const EnteredControl = ({ entered, canSet, busy, onToggle }) => {
  if (entered) {
    const base = {
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "#ECFDF5", color: "#059669", border: "1px solid #6EE7B7",
      borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
    };
    if (!canSet) {
      return <span style={base}><FeatherIcon icon="check-circle" size={10} /> Entered</span>;
    }
    return (
      <Tooltip title="Entered in accounting — click to clear" arrow>
        <button
          type="button" disabled={busy} onClick={onToggle}
          style={{ ...base, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}
        >
          <FeatherIcon icon="check-circle" size={10} /> Entered
        </button>
      </Tooltip>
    );
  }
  if (!canSet) return null;
  return (
    <Tooltip title="Mark as entered in accounting" arrow>
      <button
        type="button" disabled={busy} onClick={onToggle}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          background: "#EEF2FF", color: "#4338CA", border: "2px solid #A5B4FC",
          borderRadius: 12, padding: "3px 10px", fontSize: "0.72rem", fontWeight: 800,
          cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
          boxShadow: "0 0 0 3px rgba(99,102,241,0.15)",
        }}
      >
        <FeatherIcon icon="book-open" size={11} /> Mark entered
      </button>
    </Tooltip>
  );
};

const StatusBadge = ({ status }) => {
  const cfg = statusStyle(status);
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
        borderRadius: 20, padding: "3px 10px", fontSize: "0.72rem",
        fontWeight: 700, whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
      {status}
    </span>
  );
};

const StatusDropdown = ({ invoice, onStatusChange }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const cfg = statusStyle(invoice.status);

  const positionMenu = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menuH = menuRef.current?.offsetHeight || 200;
    const spaceBelow = window.innerHeight - r.bottom;
    const openUp = spaceBelow < menuH + 12 && r.top > spaceBelow;
    setCoords({
      top: openUp ? Math.max(8, r.top - menuH - 6) : r.bottom + 6,
      left: r.left,
      width: r.width,
    });
  };

  useLayoutEffect(() => {
    if (open) positionMenu();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onScrollOrResize = () => setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const choose = async (newStatus) => {
    setOpen(false);
    if (newStatus === invoice.status) return;

    const res = await Swal.fire({
      title: `Change status to "${newStatus}"?`,
      text: "Are you sure?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;

    setLoading(true);
    try {
      const result = await updateInvoiceStatus(invoice._id, { status: newStatus });
      onStatusChange(invoice._id, result?.data || { ...invoice, status: newStatus });
    } catch {
      toast.error("Failed to update status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: "inline-block" }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        disabled={loading}
        onClick={() => setOpen((o) => !o)}
        className="iv-status-trigger"
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
          borderRadius: 20, padding: "4px 10px", fontSize: "0.73rem", fontWeight: 700,
          cursor: loading ? "not-allowed" : "pointer", minWidth: 116,
          justifyContent: "space-between", opacity: loading ? 0.7 : 1,
          transition: "box-shadow 0.12s, transform 0.12s",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
          {invoice.status}
        </span>
        {loading ? (
          <Spinner size="sm" style={{ color: cfg.color, width: 11, height: 11 }} />
        ) : (
          <svg width="9" height="5" viewBox="0 0 9 5" fill="none" style={{ transition: "transform 0.12s", transform: open ? "rotate(180deg)" : "none" }}>
            <path d="M1 1L4.5 4L8 1" stroke={cfg.color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="iv-status-menu"
          style={{
            position: "fixed", top: coords.top, left: coords.left,
            minWidth: Math.max(coords.width, 150), zIndex: 13000,
          }}
        >
          {STATUS_VALUES.map((s) => {
            const sc = statusStyle(s);
            const active = s === invoice.status;
            return (
              <button
                key={s}
                type="button"
                className="iv-status-option"
                onClick={() => choose(s)}
                style={{ background: active ? sc.bg : "#fff" }}
              >
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: sc.color, flexShrink: 0 }} />
                <span style={{ color: sc.color, fontWeight: 700, fontSize: "0.78rem", flex: 1, textAlign: "left" }}>{s}</span>
                {active && <FeatherIcon icon="check" size={13} color={sc.color} />}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
};

// Inline approve / decline / mark-paid controls shown to the right person at the
// right stage of the workflow — clear labelled buttons, not bare icons.
const wfPill = (busy) => ({
  display: "inline-flex", alignItems: "center", gap: 5,
  borderRadius: 8, padding: "5px 12px", fontSize: "0.74rem", fontWeight: 700,
  cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
  whiteSpace: "nowrap", transition: "filter 0.12s, box-shadow 0.12s",
});

const WorkflowActions = ({ invoice, canApprove, canMarkPaid, busy, onApprove, onDecline, onMarkPaid }) => {
  const isPending = invoice.status === "Pending";
  const isApproved = invoice.status === "Approved";
  if (canApprove && isPending) {
    return (
      <div style={{ display: "inline-flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
        <button
          type="button" disabled={busy} onClick={() => onApprove(invoice)}
          className="iv-wf-btn"
          style={{ ...wfPill(busy), background: "#ECFDF5", border: "1px solid #6EE7B7", color: "#059669" }}
        >
          <FeatherIcon icon="check" size={13} /> Approve
        </button>
        <button
          type="button" disabled={busy} onClick={() => onDecline(invoice)}
          className="iv-wf-btn"
          style={{ ...wfPill(busy), background: "#FEF2F2", border: "1px solid #FECACA", color: "#DC2626" }}
        >
          <FeatherIcon icon="x" size={13} /> Decline
        </button>
      </div>
    );
  }
  if (canMarkPaid && isApproved) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <button
          type="button" disabled={busy} onClick={() => onMarkPaid(invoice)}
          className="iv-wf-btn"
          style={{ ...wfPill(busy), background: "#059669", border: "1px solid #059669", color: "#fff" }}
        >
          <FeatherIcon icon="dollar-sign" size={13} /> Mark Paid
        </button>
      </div>
    );
  }
  return null;
};

const DocumentsPreviewModal = ({ isOpen, toggle, invoice }) => {
  const [signedUrls, setSignedUrls] = useState({});
  const [active, setActive] = useState(null);
  const keys = invoice?.attachments || [];

  useEffect(() => {
    if (!isOpen || keys.length === 0) {
      setSignedUrls({});
      setActive(null);
      return;
    }
    (async () => {
      const urls = await getInvoiceSignedUrls(keys);
      setSignedUrls(urls);
      setActive(keys[0]);
    })();
  }, [isOpen, invoice?._id]); // eslint-disable-line

  const activeUrl = active ? signedUrls[active] : null;
  const activeType = active ? getFileTypeFromKey(active) : "unknown";
  const activeName = active ? active.split("/").pop() : "Documents";

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 12000 }}>
      <ModalHeader toggle={toggle} style={{ background: "#0F172A", borderBottom: "none", padding: "14px 20px" }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: "0.9rem" }}>
          <FeatherIcon icon="paperclip" size={15} style={{ marginRight: 6, verticalAlign: "middle" }} />
          {invoice?.title ? `${invoice.title} — Documents` : "Documents"}
        </span>
      </ModalHeader>
      <ModalBody style={{ padding: 0, background: "#0F172A" }}>
        {keys.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", color: "#94A3B8", gap: 10 }}>
            <FeatherIcon icon="file" size={36} />
            <div>No documents attached.</div>
          </div>
        ) : (
          <div style={{ display: "flex", height: "72vh" }}>
            <div style={{
              width: 96, flexShrink: 0, background: "#0B1220", padding: 10,
              display: "flex", flexDirection: "column", gap: 8, overflowY: "auto",
            }}>
              {keys.map((key) => {
                const t = getFileTypeFromKey(key);
                const url = signedUrls[key];
                const isActive = active === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActive(key)}
                    title={key.split("/").pop()}
                    style={{
                      width: "100%", height: 70, borderRadius: 8, overflow: "hidden",
                      border: isActive ? "2px solid #6366F1" : "2px solid transparent",
                      background: "#1E293B", cursor: "pointer", display: "flex",
                      alignItems: "center", justifyContent: "center", padding: 0,
                    }}
                  >
                    {t === "image" && url
                      ? <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <FeatherIcon icon={t === "pdf" ? "file-text" : "file"} size={22} color={t === "pdf" ? "#F87171" : "#94A3B8"} />}
                  </button>
                );
              })}
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {!activeUrl ? (
                <Spinner style={{ color: "#A5B4FC" }} />
              ) : (
                <Previewer signedUrl={activeUrl} type={activeType} height="72vh" width="100%" />
              )}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter style={{ background: "#0F172A", border: "none", padding: "12px 20px" }}>
        <Button color="secondary" onClick={toggle}>Close</Button>
        {activeUrl && (
          <a
            href={activeUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={activeName}
            className="iv-btn iv-btn--primary"
            style={{ textDecoration: "none" }}
          >
            <FeatherIcon icon="download" size={14} style={{ marginRight: 4 }} />
            Download
          </a>
        )}
      </ModalFooter>
    </Modal>
  );
};

// Compact status summary chips for a vendor's grouped invoice (e.g. "2 Approved").
const GroupStatusSummary = ({ counts }) => (
  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
    {Object.entries(counts).map(([status, n]) => {
      const cfg = statusStyle(status);
      return (
        <span key={status} style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
          borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
        }}>
          {n} {status}
        </span>
      );
    })}
  </div>
);

const InvoicesTable = ({
  invoices: initialInvoices,
  loading,
  properties,
  vendors,
  userRole,
  userLead = false,
  vendorEligibility = null,
  onRefresh,
  filters,
}) => {
  const [invoices, setInvoices] = useState(initialInvoices);
  const [formOpen, setFormOpen] = useState(false);
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerInvoice, setDrawerInvoice] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  const [forwardInvoice, setForwardInvoice] = useState(null);
  const [docsOpen, setDocsOpen] = useState(false);
  const [docsInvoice, setDocsInvoice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [restoreDraft, setRestoreDraft] = useState(false);
  const draftPromptRef = useRef(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  // Measured width of the sticky Actions column so the sticky Files column can
  // pin flush against its left edge (button count — and thus width — varies by role).
  const actionsThRef = useRef(null);
  const [actionsWidth, setActionsWidth] = useState(0);

  useEffect(() => {
    setInvoices(initialInvoices);
    setSelectedIds(new Set());
  }, [initialInvoices]);

  const role = (userRole || "").toLowerCase();
  const isCompany = role === "company";
  const isVendor = role === "vendor";
  const isAuditor = role === "auditor";
  const isManager = role === "manager";
  const isLead = role === "technical staff" && !!userLead;

  // Technical leads author invoices for their assigned properties too
  // (create / edit / forward); deletion stays company-only.
  const canEdit = isCompany || isManager || isVendor || isLead;
  const canDelete = isCompany;
  const canApprove = isCompany || isLead;       // Pending → Approved / Declined
  const canMarkPaid = isCompany || isAuditor;    // Approved → Paid
  // Bulk selection: approvers tick Pending rows, payers tick Approved rows.
  const canBulkSelect = canApprove || canMarkPaid;
  const canSetEntered = isCompany || isAuditor;  // toggle entered-in-accounting
  const canChangeStatusDropdown = isCompany;     // full manual control
  // Everyone involved in the workflow can leave a note — including the lead.
  const canComment = isCompany || isManager || isVendor || isLead || isAuditor;

  useLayoutEffect(() => {
    if (isVendor || !canEdit) { setActionsWidth(0); return undefined; }
    const measure = () => {
      const w = actionsThRef.current?.getBoundingClientRect().width;
      if (w) setActionsWidth(w);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isVendor, canEdit, loading, invoices.length]);

  // Vendors generate invoices from their eligibility; internal roles pick the
  // property + building inside the modal (prefilled from the filter bar when set).
  const vendorCanCreate = isVendor && vendorEligibility?.canCreate;
  const canCreateNew = isVendor ? !!vendorCanCreate : canEdit;

  // Property list + vendor lock differ for vendor self-service.
  const formProperties = isVendor ? (vendorEligibility?.properties || []) : properties;
  const vendorName = vendorEligibility?.vendorName || "";
  const vendorCategoryName = vendorEligibility?.categoryName || "";
  const vendorCategories = vendorEligibility?.categories || [];

  const prefilledPropertyLabel = (() => {
    if (isVendor || !filters?.propertyId) return "";
    const prop = (properties || []).find((p) => p._id === filters.propertyId);
    return prop ? (prop.title || prop.name || filters.propertyId) : filters.propertyId;
  })();

  // Offer to restore an autosaved invoice draft (left behind by a session
  // timeout, crash or accidental navigation) once the user may create invoices.
  useEffect(() => {
    if (draftPromptRef.current || !canCreateNew) return;
    const draft = readInvoiceDraft();
    if (!draft?.form) return;
    draftPromptRef.current = true;
    Swal.fire({
      icon: "info",
      title: "Restore unsaved invoice?",
      html: "<p>We found an invoice you were working on but didn't get to submit. Restore it?</p>",
      showCancelButton: true,
      confirmButtonColor: "#059669",
      cancelButtonColor: "#DC2626",
      confirmButtonText: "Restore draft",
      cancelButtonText: "Discard",
    }).then((res) => {
      if (res.isConfirmed) {
        setSelected(null);
        setIsNew(true);
        setRestoreDraft(true);
        setFormOpen(true);
      } else {
        discardInvoiceDraft();
      }
    });
  }, [canCreateNew]);

  const openNew = () => {
    if (isVendor) {
      if (!vendorCanCreate) {
        toast.warning(vendorEligibility?.reason || "You are not set up to create invoices yet.");
        return;
      }
      if (!(vendorEligibility?.properties || []).length) {
        toast.warning("No properties are linked to your vendor profile yet.");
        return;
      }
    } else {
      if (!vendors || vendors.length === 0) {
        toast.warning("Add at least one vendor (Vendors tab) before creating an invoice.");
        return;
      }
    }
    setSelected(null);
    setIsNew(true);
    setFormOpen(true);
  };

  const openEdit = (invoice, e) => {
    e?.stopPropagation?.();
    setSelected(invoice);
    setIsNew(false);
    setFormOpen(true);
  };

  const openDetail = (invoice) => {
    setDrawerInvoice(invoice);
    setDrawerOpen(true);
  };

  // Auditors get a read-only full-form preview instead of the drawer.
  const openPreview = (invoice) => {
    setPreviewInvoice(invoice);
    setPreviewOpen(true);
  };

  const openDocuments = (invoice, e) => {
    e?.stopPropagation?.();
    setDocsInvoice(invoice);
    setDocsOpen(true);
  };

  const closeForm = (refresh = true) => {
    setFormOpen(false);
    setSelected(null);
    setRestoreDraft(false);
    if (refresh) onRefresh?.();
  };

  const openForward = (invoice, e) => {
    e.stopPropagation();
    setForwardInvoice(invoice);
    setForwardOpen(true);
  };

  const applyUpdated = (id, updated) => {
    setInvoices((prev) => prev.map((r) => (r._id === id ? updated : r)));
    if (drawerInvoice && drawerInvoice._id === id) setDrawerInvoice(updated);
  };

  const handleStatusChange = (id, updated) => applyUpdated(id, updated);

  const handleApprove = async (invoice) => {
    const res = await Swal.fire({
      title: "Approve this invoice?",
      text: "The property manager will be notified and it moves to the auditors for payment.",
      input: "textarea",
      inputLabel: "Note (optional)",
      inputPlaceholder: "Add a note for the record…",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    setBusyId(invoice._id);
    try {
      const result = await approveInvoice(invoice._id, { note: (res.value || "").trim() });
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success("Invoice approved");
      onRefresh?.();
    } catch { /* toast handled */ } finally { setBusyId(null); }
  };

  const handleDecline = async (invoice) => {
    const res = await Swal.fire({
      title: "Decline this invoice?",
      text: "The vendor will be notified that the invoice was not approved.",
      input: "textarea",
      inputLabel: "Reason (required)",
      inputPlaceholder: "Tell the vendor why…",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Decline",
      confirmButtonColor: "#DC2626",
      cancelButtonText: "Cancel",
      width: 460,
      inputValidator: (v) => (!v?.trim() ? "A reason is required to decline" : undefined),
    });
    if (!res.isConfirmed) return;
    setBusyId(invoice._id);
    try {
      const result = await declineInvoice(invoice._id, { reason: (res.value || "").trim() });
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success("Invoice declined");
      onRefresh?.();
    } catch { /* toast handled */ } finally { setBusyId(null); }
  };

  const handleMarkPaid = async (invoice) => {
    const res = await Swal.fire({
      title: "Mark this invoice as paid?",
      text: "The vendor and property manager will be notified.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Mark Paid",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 440,
    });
    if (!res.isConfirmed) return;
    setBusyId(invoice._id);
    try {
      const result = await markInvoicePaid(invoice._id, {});
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success("Invoice marked paid");
      onRefresh?.();
    } catch { /* toast handled */ } finally { setBusyId(null); }
  };

  const handleToggleEntered = async (invoice) => {
    const next = !invoice.enteredAcctAt;
    if (!next) {
      const res = await Swal.fire({
        title: "Clear entered-in-accounting?",
        text: "This invoice will no longer be marked as entered in accounting.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Clear",
        confirmButtonColor: "#DC2626",
        cancelButtonText: "Cancel",
        width: 440,
      });
      if (!res.isConfirmed) return;
    }
    setBusyId(invoice._id);
    try {
      const result = await setInvoiceEnteredAcct(invoice._id, next);
      if (result?.data) applyUpdated(invoice._id, result.data);
      toast.success(next ? "Marked entered in accounting" : "Cleared entered-in-accounting");
    } catch { /* toast handled */ } finally { setBusyId(null); }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectableIds = useMemo(
    () => (invoices || [])
      .filter((inv) => (canApprove && inv.status === "Pending") || (canMarkPaid && inv.status === "Approved"))
      .map((inv) => inv._id),
    [invoices, canApprove, canMarkPaid]
  );
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const toggleSelectAll = () =>
    setSelectedIds(() => (allSelectableSelected ? new Set() : new Set(selectableIds)));

  // Selected rows split by the bulk action that applies to them.
  const selection = useMemo(() => {
    const pending = { ids: [], total: 0 };
    const approved = { ids: [], total: 0 };
    for (const inv of invoices || []) {
      if (!selectedIds.has(inv._id)) continue;
      if (inv.status === "Pending") { pending.ids.push(inv._id); pending.total += payable(inv) || 0; }
      else if (inv.status === "Approved") { approved.ids.push(inv._id); approved.total += payable(inv) || 0; }
    }
    return { pending, approved, total: pending.total + approved.total };
  }, [invoices, selectedIds]);

  const handleBulkApprove = async () => {
    const ids = selection.pending.ids;
    if (ids.length === 0) return;
    const res = await Swal.fire({
      title: `Approve ${ids.length} invoice${ids.length !== 1 ? "s" : ""}?`,
      html: `<p>Total <strong>${formatCurrency(selection.pending.total)}</strong>.<br/><small style="color:#64748B">The property manager will be notified for each and they move to the auditors for payment.</small></p>`,
      input: "textarea",
      inputLabel: "Note (optional)",
      inputPlaceholder: "Add a note for the record…",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    setBulkBusy(true);
    try {
      const result = await bulkApproveInvoices(ids, (res.value || "").trim());
      const approved = result?.data?.approved?.length || 0;
      const skipped = result?.data?.skipped?.length || 0;
      toast.success(`Approved ${approved}${skipped ? ` · ${skipped} skipped` : ""}`);
      onRefresh?.();
      setSelectedIds(new Set());
    } catch {
      toast.error("Failed to approve invoices");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkPaid = async () => {
    const ids = selection.approved.ids;
    if (ids.length === 0) return;
    const res = await Swal.fire({
      title: `Mark ${ids.length} invoice${ids.length !== 1 ? "s" : ""} as paid?`,
      html: `<p>Total <strong>${formatCurrency(selection.approved.total)}</strong>.<br/><small style="color:#64748B">The vendor and property manager will be notified for each.</small></p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Mark Paid",
      confirmButtonColor: "#059669",
      cancelButtonText: "Cancel",
      width: 460,
    });
    if (!res.isConfirmed) return;
    setBulkBusy(true);
    try {
      const result = await bulkMarkInvoicesPaid(ids);
      const paid = result?.data?.paid?.length || 0;
      const skipped = result?.data?.skipped?.length || 0;
      toast.success(`Marked ${paid} paid${skipped ? ` · ${skipped} skipped` : ""}`);
      onRefresh?.();
      setSelectedIds(new Set());
    } catch {
      toast.error("Failed to mark invoices paid");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleInlineComment = async (invoice, e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: "Add a comment",
      input: "textarea",
      inputLabel: "Your comment is saved with your name, role and time",
      inputPlaceholder: "Type a note for the team…",
      showCancelButton: true,
      confirmButtonText: "Post",
      confirmButtonColor: "#4F46E5",
      cancelButtonText: "Cancel",
      width: 460,
      inputValidator: (v) => (!v?.trim() ? "Comment cannot be empty" : undefined),
    });
    if (!res.isConfirmed) return;
    try {
      const result = await addInvoiceComment(invoice._id, { comment: res.value.trim() });
      const newHistory = result?.data?.commentsHistory;
      if (newHistory) {
        const updated = { ...invoice, commentsHistory: newHistory };
        applyUpdated(invoice._id, updated);
      }
      toast.success("Comment added");
    } catch {
      toast.error("Failed to add comment");
    }
  };

  const handleDelete = async (invoice, e) => {
    e?.stopPropagation?.();
    const res = await Swal.fire({
      title: "Delete Invoice?",
      html: `<p>Delete invoice <strong>${invoice.title || `#${invoice.invoiceNo}`}</strong>?<br/><small style="color:#64748B">This cannot be undone.</small></p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#DC2626",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      width: 420,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteInvoice(invoice._id);
      onRefresh?.();
    } catch {
      toast.error("Failed to delete invoice");
    }
  };

  // Group a vendor's splits by their shared submission (groupId / invoiceNo).
  const vendorGroups = useMemo(() => {
    if (!isVendor) return null;
    const map = new Map();
    (invoices || []).forEach((inv) => {
      const key = inv.groupId || `no-${inv.invoiceNo ?? inv._id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          invoiceNo: inv.invoiceNo,
          title: inv.title,
          vendorName: inv.vendorName,
          referenceNo: inv.referenceNo,
          createdAt: inv.createdAt || inv.invoiceDate,
          items: [],
        });
      }
      map.get(key).items.push(inv);
    });
    return Array.from(map.values()).map((g) => {
      const totalSum = g.items.reduce((a, i) => a + (payable(i) || 0), 0);
      const statusCounts = g.items.reduce((acc, i) => {
        acc[i.status] = (acc[i.status] || 0) + 1;
        return acc;
      }, {});
      const overdueCount = g.items.filter(isOverdue).length;
      const nos = g.items.map((i) => i.invoiceNo).filter((n) => n != null && n !== "");
      const rdRange = nos.length
        ? (Math.min(...nos) === Math.max(...nos)
          ? `RD ${Math.min(...nos)}`
          : `RD ${Math.min(...nos)}–${Math.max(...nos)}`)
        : "—";
      const rdLabels = new Set(g.items.map((i) => formatInvoiceNo(i)));
      const refs = [...new Set(
        g.items.map((i) => (i.referenceNo || "").trim()).filter((r) => r && !rdLabels.has(r))
      )];
      return { ...g, totalSum, statusCounts, overdueCount, rdRange, refs };
    });
  }, [invoices, isVendor]);

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const colSpan = (canEdit ? 11 : 10) + (canBulkSelect ? 1 : 0);

  const newButtonLabel = isVendor ? "Generate Invoice" : "New Invoice";
  const newButtonTip = isVendor
    ? (vendorCanCreate ? "" : (vendorEligibility?.reason || "You are not set up to create invoices yet."))
    : "";

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="file-text" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Finance</div>
              <div className="iv-header__title">{isVendor ? "My Invoices" : "Invoices"}</div>
            </div>
            {invoices.length > 0 && (
              <div className="iv-header__badges">
                <span className="iv-header__badge iv-header__badge--records">
                  {invoices.length} invoice{invoices.length !== 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>
          <div className="iv-header__actions">
            <button
              type="button"
              className="iv-btn iv-btn--ghost"
              onClick={() => setPayrollOpen(true)}
            >
              <FeatherIcon icon="calendar" size={14} />
              Payments Calendar
            </button>
            {(canEdit || isVendor) && (
              <Tooltip title={newButtonTip} arrow>
                <span>
                  <button
                    type="button"
                    className="iv-btn iv-btn--primary"
                    onClick={openNew}
                    disabled={!canCreateNew}
                    style={{ opacity: canCreateNew ? 1 : 0.5, cursor: canCreateNew ? "pointer" : "not-allowed" }}
                  >
                    <FeatherIcon icon="plus" size={14} />
                    {newButtonLabel}
                  </button>
                </span>
              </Tooltip>
            )}
          </div>
        </div>

        {/* ── Vendor grouped view ─────────────────────────────────────────── */}
        {isVendor ? (
          <SyncedScroll maxHeight="calc(100vh - 320px)">
            <table className="iv-tbl" style={{ minWidth: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: 44 }} />
                  <th style={{ minWidth: 110, textAlign: "left", paddingLeft: 10 }}>Invoice No.</th>
                  <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                  <th style={{ minWidth: 70, textAlign: "center" }}>Unit</th>
                  <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Job</th>
                  <th style={{ minWidth: 64, textAlign: "center" }}>Files</th>
                  <th style={{ minWidth: 110, textAlign: "right" }}>Total</th>
                  <th style={{ minWidth: 190, textAlign: "left", paddingLeft: 10 }}>Status</th>
                  <th className="iv-actions-th" style={{ width: 96 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
                )}
                {!loading && (!vendorGroups || vendorGroups.length === 0) && (
                  <tr>
                    <td colSpan={9} className="iv-empty-cell">
                      <div className="iv-empty" style={{ minHeight: "25vh" }}>
                        <div className="iv-empty__icon-wrap"><FeatherIcon icon="file-text" size={24} color="#4F46E5" /></div>
                        <div className="iv-empty__title">No Invoices Yet</div>
                        <div className="iv-empty__sub">
                          {vendorCanCreate
                            ? 'Click "Generate Invoice" to submit your first invoice.'
                            : (vendorEligibility?.reason || "You are not set up to create invoices yet.")}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && (vendorGroups || []).map((g) => {
                  const isOpen = expanded.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      {/* Submission summary band */}
                      <tr
                        className="iv-row"
                        style={{ cursor: "pointer", background: "#F8FAFC", borderTop: "2px solid #E2E8F0" }}
                        onClick={() => toggleExpand(g.key)}
                      >
                        <td style={{ textAlign: "center" }}>
                          <FeatherIcon icon={isOpen ? "chevron-down" : "chevron-right"} size={16} color="#4F46E5" />
                        </td>
                        <td colSpan={5} style={{ textAlign: "left", paddingLeft: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                            <span style={{ fontWeight: 800, color: "#3730A3", fontSize: "0.84rem" }}>{g.rdRange}</span>
                            <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{g.title || "Untitled"}</span>
                            <span style={{
                              background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE",
                              borderRadius: 12, padding: "1px 9px", fontSize: "0.7rem", fontWeight: 700,
                            }}>
                              {g.items.length} invoice{g.items.length !== 1 ? "s" : ""}
                            </span>
                            <span style={{ fontSize: "0.74rem", color: "#64748B", fontWeight: 600 }}>
                              {g.createdAt ? moment(g.createdAt).format("MMM D, YYYY") : "—"}
                            </span>
                            {g.refs.length > 0 && (
                              <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>
                                Ref {g.refs.join(" · ")}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 800, color: "#0F172A", fontSize: "0.86rem" }}>
                          {formatCurrency(g.totalSum)}
                        </td>
                        <td style={{ textAlign: "left", paddingLeft: 10 }}>
                          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                            <GroupStatusSummary counts={g.statusCounts} />
                            {g.overdueCount > 0 && (
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FCA5A5",
                                borderRadius: 12, padding: "1px 8px", fontSize: "0.68rem", fontWeight: 700,
                              }}>
                                <FeatherIcon icon="alert-triangle" size={10} /> {g.overdueCount} Overdue
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Matches the band's own tint so the pinned column has no white gap. */}
                        <td className="iv-actions-td" style={{ background: "#F8FAFC" }} />
                      </tr>
                      {isOpen && g.items.map((inv) => {
                        const ref = (Array.isArray(inv.properties) ? inv.properties : [])[0] || {};
                        const h = Number(inv.hours) || 0;
                        const min = Number(inv.minutes) || 0;
                        const jobParts = [`${h}h${min ? ` ${min}m` : ""}`];
                        if (inv.amountPerHour !== "" && inv.amountPerHour != null) {
                          jobParts.push(`@ ${formatCurrency(Number(inv.amountPerHour) || 0)}/hr`);
                        }
                        if (Number(inv.materialCost)) {
                          jobParts.push(`+ ${formatCurrency(Number(inv.materialCost))} materials`);
                        }
                        return (
                          <tr key={inv._id} className="iv-row" style={{ cursor: "pointer" }} onClick={() => openPreview(inv)}>
                            <td />
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.82rem" }}>{formatInvoiceNo(inv)}</div>
                              {inv.referenceNo && inv.referenceNo !== formatInvoiceNo(inv) && (
                                <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>Ref {inv.referenceNo}</div>
                              )}
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.8rem" }}>{ref.title || "—"}</div>
                              <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{ref.buildingName || "—"}</div>
                            </td>
                            <td style={{ textAlign: "center", fontSize: "0.78rem", color: "#475569", fontWeight: 600 }}>
                              {ref.unitNo || "—"}
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ fontSize: "0.76rem", color: "#475569", fontWeight: 600 }}>{jobParts.join(" ")}</div>
                              <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{inv.category?.name || "—"}</div>
                            </td>
                            <td style={{ textAlign: "center", fontSize: "0.72rem", color: "#64748B" }}>
                              {inv.attachments?.length ? (
                                <button type="button" className="iv-chip iv-chip--indigo" style={{ border: "1px solid #C7D2FE" }}
                                  onClick={(e) => openDocuments(inv, e)}>
                                  <FeatherIcon icon="paperclip" size={11} />{inv.attachments.length}
                                </button>
                              ) : "—"}
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.82rem" }}>
                              {formatCurrency(payable(inv), inv.currency)}
                            </td>
                            <td style={{ textAlign: "left", paddingLeft: 10 }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                                <StatusBadge status={inv.status} />
                                {isOverdue(inv) && <OverdueChip />}
                              </div>
                              {inv.status === "Declined" && inv.declineReason && (
                                <div style={{ fontSize: "0.68rem", color: "#B91C1C", marginTop: 3 }} title={inv.declineReason}>
                                  {inv.declineReason.length > 40 ? `${inv.declineReason.slice(0, 40)}…` : inv.declineReason}
                                </div>
                              )}
                            </td>
                            {/* Vendors get view + memos only — no edit, forward or delete. */}
                            <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                              <div className="iv-action-group">
                                <Tooltip title="Preview invoice" arrow>
                                  <button type="button" className="iv-ebtn" onClick={() => openPreview(inv)}>
                                    <FeatherIcon icon="eye" size={13} color="#0284C7" />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Memos & activity" arrow>
                                  <button type="button" className="iv-ebtn" onClick={() => openDetail(inv)}>
                                    <FeatherIcon icon="message-square" size={13} color="#4F46E5" />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </SyncedScroll>
        ) : (
          /* ── Internal roles: flat per-property table ────────────────────── */
          <>
          {canBulkSelect && selectedIds.size > 0 && (
            <div style={{
              display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
              margin: "0 0 12px", padding: "10px 16px", background: "#fff",
              border: "1px solid #E2E8F0", borderRadius: 10,
              boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
            }}>
              <span style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.82rem" }}>
                {selectedIds.size} selected
              </span>
              <span style={{ color: "#CBD5E1" }}>·</span>
              <span style={{ fontWeight: 800, color: "#059669", fontSize: "0.86rem" }}>
                {formatCurrency(selection.total)}
              </span>
              <div style={{ flex: 1 }} />
              {canApprove && selection.pending.ids.length > 0 && (
                <button
                  type="button" className="iv-btn iv-btn--primary"
                  disabled={bulkBusy} onClick={handleBulkApprove}
                  style={{ opacity: bulkBusy ? 0.6 : 1, cursor: bulkBusy ? "not-allowed" : "pointer" }}
                >
                  {bulkBusy
                    ? <Spinner size="sm" style={{ width: 13, height: 13 }} />
                    : <FeatherIcon icon="check-circle" size={14} />}
                  Approve selected
                </button>
              )}
              {canMarkPaid && selection.approved.ids.length > 0 && (
                <button
                  type="button" className="iv-btn iv-btn--primary"
                  disabled={bulkBusy} onClick={handleBulkPaid}
                  style={{ opacity: bulkBusy ? 0.6 : 1, cursor: bulkBusy ? "not-allowed" : "pointer" }}
                >
                  {bulkBusy
                    ? <Spinner size="sm" style={{ width: 13, height: 13 }} />
                    : <FeatherIcon icon="dollar-sign" size={14} />}
                  Mark selected Paid
                </button>
              )}
              <button
                type="button" className="iv-btn"
                disabled={bulkBusy} onClick={() => setSelectedIds(new Set())}
                style={{ background: "#fff", border: "1px solid #E2E8F0", color: "#64748B" }}
              >
                Clear
              </button>
            </div>
          )}
          <SyncedScroll maxHeight="calc(100vh - 320px)">
            <table className="iv-tbl" style={{ minWidth: "100%" }}>
              <thead>
                <tr>
                  {canBulkSelect && (
                    <th style={{ width: 40, textAlign: "center" }}>
                      <StyledCheckbox
                        checked={allSelectableSelected}
                        disabled={selectableIds.length === 0}
                        onChange={toggleSelectAll}
                        label="Select all actionable invoices"
                      />
                    </th>
                  )}
                  <th style={{ width: 56 }}>S.No</th>
                  <th style={{ minWidth: 120 }}>Submitted On</th>
                  <th style={{ minWidth: 180, textAlign: "left", paddingLeft: 10 }}>Invoice</th>
                  <th style={{ minWidth: 150, textAlign: "left", paddingLeft: 10 }}>Vendor</th>
                  <th style={{ minWidth: 130 }}>Category</th>
                  <th style={{ minWidth: 170, textAlign: "left", paddingLeft: 10 }}>Property / Building</th>
                  <th style={{ minWidth: 120, textAlign: "right" }}>Total</th>
                  <th style={{ minWidth: 200, textAlign: "center" }}>Status</th>
                  <th style={{ minWidth: 90 }}>Comments</th>
                  {isAuditor ? (
                    <th className="iv-actions-th" style={{ width: 110 }}>Actions</th>
                  ) : (
                    <th className="iv-files-th" style={{ minWidth: 70, right: canEdit ? actionsWidth : 0 }}>Files</th>
                  )}
                  {canEdit && <th ref={actionsThRef} className="iv-actions-th" style={{ width: 120 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={colSpan} style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: "#4F46E5" }} /></td></tr>
                )}

                {!loading && invoices.length === 0 && (
                  <tr>
                    <td colSpan={colSpan} className="iv-empty-cell">
                      <div className="iv-empty" style={{ minHeight: "25vh" }}>
                        <div className="iv-empty__icon-wrap"><FeatherIcon icon="file-text" size={24} color="#4F46E5" /></div>
                        <div className="iv-empty__title">No Invoices Yet</div>
                        <div className="iv-empty__sub">
                          {canEdit
                            ? 'Click "New Invoice" to record one.'
                            : "No invoices found for the current filters."}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && invoices.map((inv, idx) => {
                  const categoryName = inv.category?.name || "—";
                  const propertyRefs = Array.isArray(inv.properties) ? inv.properties : [];
                  const attachmentCount = inv.attachments?.length || 0;
                  const commentsCount = inv.commentsHistory?.length || 0;
                  const isApproved = inv.status === "Approved";

                  return (
                    <tr key={inv._id} className="iv-row" onClick={() => (isAuditor ? openPreview(inv) : openDetail(inv))} style={{ cursor: "pointer" }}>
                      {canBulkSelect && (
                        <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                          {(canApprove && inv.status === "Pending") || (canMarkPaid && isApproved) ? (
                            <StyledCheckbox
                              checked={selectedIds.has(inv._id)}
                              onChange={() => toggleSelect(inv._id)}
                              label="Select invoice"
                            />
                          ) : null}
                        </td>
                      )}
                      <td style={{ textAlign: "center" }}>
                        <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.82rem" }}>{idx + 1}</span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span style={{ fontSize: "0.78rem", color: "#475569", fontWeight: 500 }}>{fmtSubmitted(inv)}</span>
                      </td>
                      <td style={{ textAlign: "left", paddingLeft: 10, maxWidth: 230 }}>
                        <div
                          style={{ fontSize: "0.84rem", fontWeight: 800, color: "#0F172A", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={inv.title || ""}
                        >
                          {inv.title || "Untitled"}
                        </div>
                        <div style={{ fontSize: "0.72rem", color: "#64748B", fontWeight: 600, marginTop: 1 }}>
                          ({formatInvoiceNo(inv)})
                          {inv.referenceNo && inv.referenceNo !== formatInvoiceNo(inv) ? ` · Ref ${inv.referenceNo}` : ""}
                        </div>
                      </td>
                      <td style={{ textAlign: "left", paddingLeft: 10 }}>
                        <div style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.84rem" }}>{inv.vendorName || "—"}</div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span style={{
                          display: "inline-block", background: "#EEF2FF", color: "#4338CA",
                          border: "1px solid #C7D2FE", borderRadius: 6,
                          padding: "3px 10px", fontSize: "0.75rem", fontWeight: 600,
                        }}>{categoryName}</span>
                      </td>
                      <td style={{ textAlign: "left", paddingLeft: 10 }}>
                        {propertyRefs.length === 0 ? (
                          <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.80rem" }}>—</div>
                        ) : (
                          <>
                            <div style={{ fontWeight: 600, color: "#0F172A", fontSize: "0.80rem" }}>{propertyRefs[0].title || "—"}</div>
                            <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 1 }}>{propertyRefs[0].buildingName || "—"}</div>
                          </>
                        )}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#0F172A", fontSize: "0.85rem" }}>
                        <Tooltip arrow title={`Subtotal ${formatCurrency(inv.amount, inv.currency)} + GST ${inv.gstRate ?? 0}% (${formatCurrency(inv.gstAmount || 0, inv.currency)})`}>
                          <span>{formatCurrency(payable(inv), inv.currency)}</span>
                        </Tooltip>
                      </td>
                      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
                          {canChangeStatusDropdown ? (
                            <StatusDropdown invoice={inv} onStatusChange={handleStatusChange} />
                          ) : (
                            <StatusBadge status={inv.status} />
                          )}
                          {isOverdue(inv) && <OverdueChip />}
                          <WorkflowActions
                            invoice={inv}
                            canApprove={canApprove}
                            canMarkPaid={canMarkPaid}
                            busy={busyId === inv._id}
                            onApprove={handleApprove}
                            onDecline={handleDecline}
                            onMarkPaid={handleMarkPaid}
                          />
                          {(inv.enteredAcctAt || canSetEntered) && (
                            <EnteredControl
                              entered={!!inv.enteredAcctAt}
                              canSet={canSetEntered}
                              busy={busyId === inv._id}
                              onToggle={() => handleToggleEntered(inv)}
                            />
                          )}
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {commentsCount > 0 && (
                            <Tooltip arrow placement="top" title={
                              <div style={{ maxWidth: 280, padding: "4px 2px", maxHeight: 260, overflowY: "auto" }}>
                                {(inv.commentsHistory || []).slice().reverse().map((c, i) => (
                                  <div key={c._id || i} style={{ paddingBottom: 7, marginBottom: 7, borderBottom: i < commentsCount - 1 ? "1px solid rgba(255,255,255,0.18)" : "none" }}>
                                    <div style={{ fontSize: "0.7rem", fontWeight: 700 }}>
                                      {c.author?.displayName || "Unknown"}
                                      {c.author?.role ? <span style={{ opacity: 0.75, fontWeight: 500 }}> · {c.author.role}</span> : null}
                                    </div>
                                    <div style={{ fontSize: "0.62rem", opacity: 0.7, marginBottom: 2 }}>
                                      {c.createdAt ? moment(c.createdAt).format("MMM D, YYYY · h:mm A") : ""}
                                    </div>
                                    <div style={{ fontSize: "0.72rem", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>{c.comment}</div>
                                  </div>
                                ))}
                              </div>
                            }>
                              <span className="iv-chip iv-chip--gray" style={{ cursor: "help" }}>
                                <FeatherIcon icon="message-square" size={11} />{commentsCount}
                              </span>
                            </Tooltip>
                          )}
                          {canComment ? (
                            <Tooltip title="Add comment" arrow>
                              <button type="button" className="iv-ebtn" onClick={(e) => handleInlineComment(inv, e)}>
                                <FeatherIcon icon="message-square" size={12} color="#4F46E5" />
                              </button>
                            </Tooltip>
                          ) : (commentsCount === 0 && <span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>)}
                        </div>
                      </td>
                      {isAuditor ? (
                        <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                          <div className="iv-action-group">
                            <Tooltip title="Preview invoice" arrow>
                              <button type="button" className="iv-ebtn" onClick={() => openPreview(inv)}>
                                <FeatherIcon icon="eye" size={13} color="#0284C7" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Memos & activity" arrow>
                              <button type="button" className="iv-ebtn" onClick={() => openDetail(inv)}>
                                <FeatherIcon icon="message-square" size={13} color="#4F46E5" />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      ) : (
                        <td
                          onClick={(e) => e.stopPropagation()}
                          className="iv-files-td"
                          style={{ textAlign: "center", right: canEdit ? actionsWidth : 0 }}
                        >
                          {attachmentCount > 0 ? (
                            <Tooltip title="Preview documents" arrow>
                              <button type="button" className="iv-chip iv-chip--indigo" onClick={(e) => openDocuments(inv, e)} style={{ border: "1px solid #C7D2FE", cursor: "pointer" }}>
                                <FeatherIcon icon="paperclip" size={11} />{attachmentCount}
                              </button>
                            </Tooltip>
                          ) : (<span style={{ color: "#CBD5E1", fontSize: "0.75rem" }}>—</span>)}
                        </td>
                      )}
                      {canEdit && (
                        <td className="iv-actions-td" onClick={(e) => e.stopPropagation()}>
                          <div className="iv-action-group">
                            <Tooltip title="Edit" arrow>
                              <button type="button" className="iv-ebtn" onClick={(e) => openEdit(inv, e)}>
                                <FeatherIcon icon="edit-2" size={13} color="#4F46E5" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Preview invoice" arrow>
                              <button type="button" className="iv-ebtn" onClick={() => openPreview(inv)}>
                                <FeatherIcon icon="eye" size={13} color="#0284C7" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Memos & activity" arrow>
                              <button type="button" className="iv-ebtn" onClick={() => openDetail(inv)}>
                                <FeatherIcon icon="message-square" size={13} color="#0284C7" />
                              </button>
                            </Tooltip>
                            <Tooltip title="Forward" arrow>
                              <button type="button" className="iv-ebtn" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0" }} onClick={(e) => openForward(inv, e)}>
                                <FeatherIcon icon="send" size={13} color="#059669" />
                              </button>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title="Delete" arrow>
                                <button type="button" className="iv-dbtn" onClick={(e) => handleDelete(inv, e)}>
                                  <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SyncedScroll>
          </>
        )}
      </Paper>

      <PayrollCalendarModal
        isOpen={payrollOpen}
        onClose={() => setPayrollOpen(false)}
      />

      <InvoiceFormModal
        isOpen={formOpen}
        onClose={closeForm}
        invoiceData={selected}
        isNew={isNew}
        properties={formProperties}
        vendors={vendors}
        vendorLocked={isVendor}
        vendorName={vendorName}
        vendorCategoryName={vendorCategoryName}
        vendorCategories={vendorCategories}
        prefilledPropertyId={isVendor ? "" : (filters?.propertyId || "")}
        prefilledBuildingName={isVendor ? "" : (filters?.buildingName || "")}
        prefilledPropertyLabel={prefilledPropertyLabel}
        restoreDraft={restoreDraft}
      />

      {/* Read-only preview (auditors: row click / eye action) */}
      <InvoiceFormModal
        isOpen={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewInvoice(null); }}
        invoiceData={previewInvoice}
        isNew={false}
        readOnly
        properties={formProperties}
        vendors={vendors}
      />

      <InvoiceDetailDrawer
        isOpen={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerInvoice(null); }}
        invoice={drawerInvoice}
        canEdit={canEdit}
        canApprove={canApprove}
        canMarkPaid={canMarkPaid}
        canSetEntered={canSetEntered}
        busy={busyId === drawerInvoice?._id}
        onApprove={handleApprove}
        onDecline={handleDecline}
        onMarkPaid={handleMarkPaid}
        onChange={(updated) => {
          setDrawerInvoice(updated);
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
        }}
        onForward={() => { setForwardInvoice(drawerInvoice); setForwardOpen(true); }}
        onEdit={() => {
          // Close the drawer, then open the edit modal on the next tick so the
          // modal can never mount behind the drawer or race its unmount.
          const inv = drawerInvoice;
          setDrawerOpen(false);
          setDrawerInvoice(null);
          setTimeout(() => {
            setSelected(inv);
            setIsNew(false);
            setFormOpen(true);
          }, 0);
        }}
        onPreview={() => {
          const inv = drawerInvoice;
          setDrawerOpen(false);
          setDrawerInvoice(null);
          setTimeout(() => openPreview(inv), 0);
        }}
      />

      <InvoiceForwardModal
        isOpen={forwardOpen}
        onClose={() => { setForwardOpen(false); setForwardInvoice(null); }}
        invoice={forwardInvoice}
        onForwardSuccess={(updatedLog) => {
          if (!forwardInvoice) return;
          const updated = { ...forwardInvoice, communicationLog: updatedLog };
          setInvoices((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));
          if (drawerInvoice && drawerInvoice._id === updated._id) setDrawerInvoice(updated);
        }}
      />

      <DocumentsPreviewModal
        isOpen={docsOpen}
        toggle={() => { setDocsOpen(false); setDocsInvoice(null); }}
        invoice={docsInvoice}
      />
    </>
  );
};

export { InvoicesTable };
